import { CheckCircle, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  TextField,
  Typography,
} from '@mui/material';
import { signInWithEmailAndPassword, updatePassword } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { auth } from '../firebase';
import { colors } from '../theme';

const SetPasswordPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const tempPassParam = searchParams.get('temp');

    if (emailParam) setEmail(emailParam);
    if (tempPassParam) setTempPassword(tempPassParam);
  }, [searchParams]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validations
    if (!email || !tempPassword || !newPassword || !confirmPassword) {
      setError('Lütfen tüm alanları doldurun');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Yeni şifre en az 6 karakter olmalıdır');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Şifreler eşleşmiyor');
      setLoading(false);
      return;
    }

    try {
      // Önceki kullanıcının verilerini temizle
      const previousEmail = localStorage.getItem('userEmail');
      if (previousEmail && previousEmail !== email) {
        console.log('🔄 Farklı kullanıcı şifre ayarlıyor, eski veriler temizleniyor...');
        // Tüm kullanıcıya özel localStorage verilerini temizle
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
        localStorage.removeItem('dietitianData');
        localStorage.removeItem('clientData');
        localStorage.removeItem('client-onboarding-completed');
        
        // TaroBot mesajlarını temizle
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('taro-messages-')) {
            localStorage.removeItem(key);
          }
          // Bildirimleri temizle
          if (key.startsWith('notifications_') || key.startsWith('processedMessages_')) {
            localStorage.removeItem(key);
          }
        });
      }

      // 1. Geçici şifre ile giriş yap
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        tempPassword
      );

      // 2. Yeni şifreyi ayarla
      if (userCredential.user) {
        await updatePassword(userCredential.user, newPassword);

        // 3. Backend'de client olup olmadığını kontrol et
        try {
          const { clientApi } = await import('../services/clientApi');
          await clientApi.getClientByEmail(email);
        } catch (apiError) {
          // Kullanıcı client koleksiyonunda bulunamadı
          await auth.signOut();
          setError(
            'Bu email ile kayıtlı danışan bulunamadı. Lütfen diyetisyeninizle iletişime geçin.'
          );
          showError('Bu email ile kayıtlı danışan bulunamadı.', 'Hata');
          setLoading(false);
          return;
        }

        setSuccess(true);
        showSuccess(
          'Şifreniz başarıyla ayarlandı! Yönlendiriliyorsunuz...',
          'Başarılı'
        );

        // 4. Client dashboard'a yönlendir
        setTimeout(() => {
          localStorage.setItem('userEmail', email);
          localStorage.setItem('userRole', 'client');
          navigate('/client');
        }, 2000);
      }
    } catch (error: any) {
      let errorMessage = 'Bir hata oluştu. Lütfen tekrar deneyin.';

      if (error.code === 'auth/invalid-email') {
        errorMessage = 'Geçersiz e-posta adresi.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'Bu e-posta ile kayıtlı kullanıcı bulunamadı.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Geçici şifre hatalı.';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'E-posta veya geçici şifre hatalı.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Şifre en az 6 karakter olmalıdır.';
      }

      setError(errorMessage);
      showError(errorMessage, 'Şifre Ayarlama Hatası');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #66BB6A 0%, #43A047 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={6}>
          <CardContent sx={{ p: 4 }}>
            {/* Başlık */}
            <Box textAlign="center" mb={4}>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Şifre Belirle
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Yeni şifrenizi oluşturun ve hesabınıza giriş yapın
              </Typography>
            </Box>

            {/* Success Message */}
            {success && (
              <Alert
                severity="success"
                icon={<CheckCircle />}
                sx={{ mb: 3, animation: 'fadeIn 0.3s ease-in' }}
              >
                <Typography fontWeight={600}>
                  Şifreniz başarıyla ayarlandı!
                </Typography>
                <Typography variant="body2">
                  Giriş sayfasına yönlendiriliyorsunuz...
                </Typography>
              </Alert>
            )}

            {/* Error Message */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Form */}
            <form onSubmit={handleSetPassword}>
              <TextField
                fullWidth
                label="E-posta"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                disabled={loading || success}
                InputProps={{
                  readOnly: !!searchParams.get('email'),
                }}
              />

              <TextField
                fullWidth
                label="Geçici Şifre"
                type={showTempPassword ? 'text' : 'password'}
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                margin="normal"
                required
                disabled={loading || success}
                helperText="Diyetisyeninizden aldığınız geçici şifre"
                InputProps={{
                  readOnly: !!searchParams.get('temp'),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowTempPassword(!showTempPassword)}
                        edge="end"
                      >
                        {showTempPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Yeni Şifre"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                required
                disabled={loading || success}
                helperText="En az 6 karakter"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Yeni Şifre (Tekrar)"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                disabled={loading || success}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || success}
                sx={{
                  mt: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  background:
                    'linear-gradient(135deg, #66BB6A 0%, #43A047 100%)',
                  '&:hover': {
                    background:
                      'linear-gradient(135deg, #43A047 0%, #388E3C 100%)',
                  },
                }}
              >
                {loading ? 'Ayarlanıyor...' : 'Şifremi Ayarla'}
              </Button>
            </form>

            {/* Alt Bilgi */}
            <Box textAlign="center" mt={3}>
              <Typography variant="caption" color="text.secondary">
                Zaten hesabınız var mı?{' '}
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/client/login')}
                  disabled={loading || success}
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 600,
                    textDecoration: 'none',
                    cursor: loading || success ? 'not-allowed' : 'pointer',
                    background: 'transparent',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: 1,
                    transition: 'all 0.2s ease',
                    opacity: loading || success ? 0.6 : 1,
                    '&:hover': {
                      color: colors.primary.dark,
                      backgroundColor: 'rgba(67, 160, 71, 0.08)',
                      textDecoration: 'none',
                    },
                    '&:active': {
                      backgroundColor: 'rgba(67, 160, 71, 0.12)',
                    },
                  }}
                >
                  Giriş Yap
                </MuiLink>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default SetPasswordPage;
